import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type StatStripTone = 'brand' | 'success' | 'warning' | 'error' | 'muted';

export interface StatStripCell {
  icon: ReactNode;
  // Long label shown on sm+ widths.
  label: string;
  // Short label shown on mobile (UPPERCASE via the inner styling).
  shortLabel: string;
  value: number | string;
  tone: StatStripTone;
  onClick?: () => void;
}

interface StatStripProps {
  cells: StatStripCell[];
  className?: string;
}

// Slim N-cell horizontal strip used for "the page's headline numbers".
// Originally extracted from PropertyStatStrip; reused on the Alerts page
// and on the Dashboard mobile view so QA's "less scrolling to the
// pertinent information" feedback applies consistently across surfaces.
//
// Visual rules:
//   - Mobile (default): icon stacks above value+label; smaller padding.
//   - sm+: icon-left, value+label-right, more breathing room.
//   - Cell tone drives icon background, value color (warning/error
//     keep the accent color), and click-hover tint when the cell is
//     interactive.
export function StatStrip({ cells, className }: StatStripProps) {
  return (
    <div
      className={cn(
        'grid divide-x divide-gray-200 dark:divide-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden',
        // Auto-flow into N equal columns based on the cells array. Most
        // callers pass 3 (the historical PropertyStatStrip shape) but
        // the layout doesn't hardcode the count.
        cells.length === 3 && 'grid-cols-3',
        cells.length === 2 && 'grid-cols-2',
        cells.length === 4 && 'grid-cols-4',
        cells.length === 5 && 'grid-cols-5',
        className,
      )}
    >
      {cells.map((cell, idx) => (
        <Cell key={idx} {...cell} />
      ))}
    </div>
  );
}

function Cell({ icon, label, shortLabel, value, tone, onClick }: StatStripCell) {
  const iconWrapClass = cn(
    'flex-shrink-0 rounded-lg flex items-center justify-center transition-colors',
    'w-7 h-7 sm:w-9 sm:h-9',
    tone === 'brand' && 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300',
    tone === 'success' && 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400',
    tone === 'warning' && 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
    tone === 'error' && 'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400',
    tone === 'muted' && 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500',
  );
  const valueClass = cn(
    'font-semibold leading-none tabular-nums',
    'text-base sm:text-xl',
    tone === 'warning' && 'text-warning-700 dark:text-warning-400',
    tone === 'error' && 'text-error-700 dark:text-error-400',
    (tone === 'brand' || tone === 'success' || tone === 'muted') && 'text-gray-900 dark:text-gray-100',
  );

  const Inner = (
    <div className="flex flex-col items-center text-center gap-1 px-2 py-3 sm:flex-row sm:items-center sm:text-left sm:gap-3 sm:px-5 sm:py-3.5">
      <div className={iconWrapClass}>{icon}</div>
      <div className="min-w-0">
        <p className={valueClass}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-[9px] sm:text-[11px] font-medium uppercase tracking-wide sm:tracking-wider text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 whitespace-nowrap">
          <span className="sm:hidden">{shortLabel}</span>
          <span className="hidden sm:inline">{label}</span>
        </p>
      </div>
    </div>
  );

  if (onClick) {
    // Hover/focus tint matches the cell's tone so the affordance reads
    // as "click into the same severity" rather than a mismatched accent.
    const hoverClass =
      tone === 'error'
        ? 'hover:bg-error-50/40 dark:hover:bg-error-500/5 focus-visible:bg-error-50 dark:focus-visible:bg-error-500/10'
        : tone === 'warning'
          ? 'hover:bg-warning-50/40 dark:hover:bg-warning-500/5 focus-visible:bg-warning-50 dark:focus-visible:bg-warning-500/10'
          : tone === 'success'
            ? 'hover:bg-success-50/40 dark:hover:bg-success-500/5 focus-visible:bg-success-50 dark:focus-visible:bg-success-500/10'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 focus-visible:bg-gray-100 dark:focus-visible:bg-gray-700';
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn('text-left transition-colors focus:outline-none', hoverClass)}
      >
        {Inner}
      </button>
    );
  }

  return <div>{Inner}</div>;
}
